/**
 * 도면 배치 검사 — 만들 수 없는 배치를 찾아 문장으로 알려 줌
 * 화면은 이 결과로 (1) 새로 놓거나 고칠 때 그 요소에 문제가 생기면 막고 (2) 나중에 생긴 문제(모양·크기 변경 등)는 경고 목록으로 보여 줌
 *
 * 좌표: 세계 좌표 mm (0,0 = 바깥 왼쪽 위), 외곽선 = 패널 바깥면, 벽 패널은 외곽선 안쪽 PT 띠
 */
import { Door, Hari, MiddleWall, Drain } from '../types';
import { Pt, Notch, Opening, rectWithNotches, harisToNotches, partitionSegments } from '../engine/polyEngine';
import { mainHarisToEngine } from '../engine/polyRender';
import { validateCuts } from '../components/ShapeEditor';
import { sideFaces, checkDoor, doorFrom, doorLen, Range } from './doorPlacement';

export type IssueTarget = 'shape' | 'hari' | 'door' | 'partition' | 'drain' | 'wall';
export interface PlanIssue { target: IssueTarget; id?: string; msg: string }

export interface PlanInput {
  H: number; V: number; PT: number; min: number;
  cuts: Notch[]; haris: Hari[]; doors: Door[]; middleWalls: MiddleWall[]; drains: Drain[]; existing: Opening[];
}

const SIDE: Record<string, string> = { top: '상단', bottom: '하단', left: '좌측', right: '우측' };
const CORNER: Record<string, string> = { topLeft: '왼쪽 위', topRight: '오른쪽 위', bottomLeft: '왼쪽 아래', bottomRight: '오른쪽 아래' };
type Box = [number, number, number, number]; // x0 y0 x1 y1

/** 홈(잘라낸 모양·하리) → 세계 상자 */
export function notchBox(n: Notch, H: number, V: number): Box {
  if (n.type === 'corner') {
    const x0 = n.corner.endsWith('Left') ? 0 : H - n.w, y0 = n.corner.startsWith('top') ? 0 : V - n.h;
    return [x0, y0, x0 + n.w, y0 + n.h];
  }
  const [a0, a1] = [Math.min(n.from, n.to), Math.max(n.from, n.to)];
  if (n.side === 'top') return [a0, 0, a1, n.depth];
  if (n.side === 'bottom') return [a0, V - n.depth, a1, V];
  if (n.side === 'left') return [0, a0, n.depth, a1];
  return [H - n.depth, a0, H, a1];
}
const overlap = (a: Box, b: Box) => Math.min(a[2], b[2]) - Math.max(a[0], b[0]) > 0 && Math.min(a[3], b[3]) - Math.max(a[1], b[1]) > 0;

export const hariName = (h: Hari) => (h.hariType === 'corner' ? `하리(${CORNER[h.corner ?? ''] ?? '코너'})` : `하리(${SIDE[h.wallSide ?? ''] ?? ''} ${h.position ?? 0})`);
export const doorName = (d: Door) => `문(${SIDE[d.wallSide]} ${d.width})`;
const mwName = (m: MiddleWall) => `칸막이(${m.direction === 'vertical' ? '세로' : '가로'} ${m.position})`;

/** 점이 다각형 안 (짝홀) */
function inside(p: Pt, pts: Pt[]) {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) c = !c;
  }
  return c;
}
/** 점 ↔ 선분 거리 */
function segDist(p: Pt, a: Pt, b: Pt) {
  const dx = b.x - a.x, dy = b.y - a.y, L = dx * dx + dy * dy;
  const t = L ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function checkPlan(p: PlanInput): PlanIssue[] {
  const { H, V, PT, min } = p;
  const out: PlanIssue[] = [];

  // 1. 잘라낸 모양 (틀리면 화면은 직사각형으로 그림)
  const cutErrs = validateCuts(H, V, p.cuts);
  cutErrs.forEach(e => out.push({ target: 'shape', msg: `모양 자르기 ${e} — 직사각형으로 그렸습니다` }));
  const cuts = cutErrs.length ? [] : p.cuts;

  // 2. 하리: 크기·도면 밖·서로/모양과 겹침
  const hNotch = harisToNotches(mainHarisToEngine(p.haris, H, V, PT), PT);
  const hBox = hNotch.map(n => notchBox(n, H, V));
  const cBox = cuts.map(n => notchBox(n, H, V));
  p.haris.forEach((h, i) => {
    const b = hBox[i], nm = hariName(h);
    if (b[2] - b[0] <= 0 || b[3] - b[1] <= 0) out.push({ target: 'hari', id: h.id, msg: `${nm}: 크기가 벽 두께(${PT})보다 작음` });
    else if (b[0] < 0 || b[1] < 0 || b[2] > H || b[3] > V) out.push({ target: 'hari', id: h.id, msg: `${nm}: 도면 밖으로 나감` });
    else if (b[2] - b[0] > H - 1000 || b[3] - b[1] > V - 1000) out.push({ target: 'hari', id: h.id, msg: `${nm}: 너무 큼 (남는 폭 1000mm 이상 필요)` });
    for (let k = i + 1; k < p.haris.length; k++) if (overlap(b, hBox[k])) out.push({ target: 'hari', id: h.id, msg: `${nm}·${hariName(p.haris[k])} 겹침` });
    cBox.forEach((c, k) => { if (overlap(b, c)) out.push({ target: 'hari', id: h.id, msg: `${nm}: 잘라낸 모양 ${k + 1}번과 겹침` }); });
  });

  const ol = rectWithNotches(H, V, [...cuts, ...hNotch]), outline = ol.pts;

  // 3. 외곽 변이 최소 패널보다 짧음 (하리·홈 깊이가 너무 얕거나 좁음)
  outline.forEach((a, i) => {
    const b = outline[(i + 1) % outline.length], L = Math.round(Math.hypot(b.x - a.x, b.y - a.y));
    if (L > 0 && L < min) out.push({ target: 'wall', msg: `벽 조각 ${L}mm (${Math.round(a.x)},${Math.round(a.y)} 근처) — 최소 패널 ${min}mm보다 짧음` });
  });

  // 4. 문: 하리·모양·크기가 바뀐 뒤에도 놓일 수 있는지
  p.doors.forEach(d => {
    const faces = sideFaces(outline, d.wallSide, H, V, PT);
    const others: Range[] = p.doors.filter(o => o.id !== d.id && o.wallSide === d.wallSide).map(o => ({ lo: doorFrom(o, PT), hi: doorFrom(o, PT) + doorLen(o) }));
    const c = checkDoor(d.wallSide, doorFrom(d, PT), doorLen(d), faces, others, min);
    if (!c.ok) out.push({ target: 'door', id: d.id, msg: `${doorName(d)}: ${c.reason}` });
    // 기존 벽(패널 없음) 구간에는 문 패널을 넣을 수 없음
    const lo = doorFrom(d, PT), hi = lo + doorLen(d);
    const onExisting = p.existing.some(o => {
      if (o.kind !== 'existing' || !o.edge) return false;
      const [[x0, y0], [x1, y1]] = o.edge.split('-').map(t => t.split(',').map(Number));
      const side = y0 === y1 ? (y0 === 0 ? 'top' : y0 === V ? 'bottom' : null) : (x0 === 0 ? 'left' : x0 === H ? 'right' : null);
      return side === d.wallSide && Math.min(o.from, o.to) < hi && Math.max(o.from, o.to) > lo;
    });
    if (onExisting) out.push({ target: 'door', id: d.id, msg: `${doorName(d)}: 기존 벽 구간과 겹침` });
  });

  // 5. 칸막이: 실내인지, 벽·다른 칸막이와 겹침, 문에 닿음, 짧은 조각
  const parts = p.middleWalls.map(m => ({ id: m.id, dir: (m.direction === 'vertical' ? 'v' : 'h') as 'v' | 'h', pos: PT + m.position, anchor: m.anchor, thick: m.panelThick }));
  const segs = partitionSegments(ol, PT, parts, p.existing);
  const doorRanges = p.doors.map(d => ({ side: d.wallSide, lo: doorFrom(d, PT), hi: doorFrom(d, PT) + doorLen(d) }));
  p.middleWalls.forEach((m, i) => {
    const pt = parts[i], t = pt.thick ?? PT, nm = mwName(m);
    const mine = segs.filter(s => s.id.startsWith(`${m.id}#`));
    if (!mine.length) { out.push({ target: 'partition', id: m.id, msg: `${nm}: 실내가 아닌 위치` }); return; }
    // 평행한 외곽 변(벽 패널 띠)과 겹침
    const lo = Math.min(...mine.map(s => s.from)), hi = Math.max(...mine.map(s => s.to));
    outline.forEach((a, k) => {
      const b = outline[(k + 1) % outline.length];
      const parallel = pt.dir === 'v' ? a.x === b.x : a.y === b.y;
      if (!parallel) return;
      const c = pt.dir === 'v' ? a.x : a.y, e0 = pt.dir === 'v' ? Math.min(a.y, b.y) : Math.min(a.x, b.x), e1 = pt.dir === 'v' ? Math.max(a.y, b.y) : Math.max(a.x, b.x);
      if (e1 <= lo || e0 >= hi) return;
      if (Math.abs(c - pt.pos) < PT + t / 2) out.push({ target: 'partition', id: m.id, msg: `${nm}: 벽 패널과 겹침 (벽에서 ${Math.round(Math.abs(c - pt.pos) - t / 2)}mm)` });
    });
    // 다른 평행 칸막이와 겹침
    for (let k = i + 1; k < p.middleWalls.length; k++) {
      const q = parts[k];
      if (q.dir !== pt.dir || Math.abs(q.pos - pt.pos) >= (t + (q.thick ?? PT)) / 2) continue;
      const theirs = segs.filter(s => s.id.startsWith(`${q.id}#`));
      if (theirs.some(s => mine.some(o => Math.min(o.to, s.to) - Math.max(o.from, s.from) > 0)))
        out.push({ target: 'partition', id: m.id, msg: `${nm}·${mwName(p.middleWalls[k])} 겹침` });
    }
    // 끝이 바깥 벽의 문에 닿음 (칸막이 몸통 폭이 문 구간과 겹침)
    mine.forEach(s => {
      const ends: [string, number][] = pt.dir === 'v' ? [['top', s.from - PT], ['bottom', s.to + PT]] : [['left', s.from - PT], ['right', s.to + PT]];
      ends.forEach(([side, line]) => {
        const onEdge = side === 'top' || side === 'left' ? Math.abs(line) < 1 : Math.abs(line - (side === 'bottom' ? V : H)) < 1;
        if (!onEdge) return;
        const d = doorRanges.find(r => r.side === side && r.lo < pt.pos + t / 2 && r.hi > pt.pos - t / 2);
        if (d) out.push({ target: 'partition', id: m.id, msg: `${nm}: 끝이 ${SIDE[side]} 문에 닿음` });
      });
      const L = Math.round(s.to - s.from);
      if (L > 1 && L < min) out.push({ target: 'partition', id: m.id, msg: `${nm}: 조각 ${L}mm — 최소 패널 ${min}mm보다 짧음` });
    });
  });

  // 6. 배수구: 바닥(벽 패널 안쪽) 안
  p.drains.forEach((d, i) => {
    const pt = { x: d.x, y: d.y };
    const near = outline.some((a, k) => segDist(pt, a, outline[(k + 1) % outline.length]) < PT);
    if (!inside(pt, outline) || near) out.push({ target: 'drain', id: d.id, msg: `배수구 ${i + 1}: 바닥 밖 (벽 위·하리·잘라낸 곳)` });
  });

  return out;
}

/**
 * 요소(id) 하나를 놓거나 고친 뒤 새로 생긴 문제 — 놓기·고치기를 막을 때
 * 다른 요소에 생긴 문제도 포함 (하리를 문 위에 놓으면 문제는 문 쪽에 생김).
 * 이미 문제가 있던 요소를 고치는 중이면 그 요소 자신의 문제는 막지 않음 (고치다 만 상태로라도 손댈 수 있게)
 */
export function newIssuesFor(before: PlanIssue[], after: PlanIssue[], id: string): PlanIssue[] {
  const had = new Set(before.map(i => i.msg));
  const wasBad = before.some(i => i.id === id);
  return after.filter(i => !had.has(i.msg) && !(wasBad && i.id === id));
}
